import { twMerge } from "tailwind-merge"

export default {
  // Default variant - Standard table layout
  default: {
    container: "flex flex-col h-full",
    header: "flex flex-row justify-left items-center mb-2",
    title: "text-text text-xl font-semibold",
    searchInput:
      "text-text bg-transparent outline-none ml-10 flex-1 w-[200px] h-[30px] px-2 py-1 rounded",
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-auto flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper",
    table: "w-full table-fixed md:table-auto",
    thead: "sticky top-0 z-10 bg-paper text-text",
    headerRow: "text-left bg-neutral_1/50 text-text font-normal",
    headerCell: "px-2 py-2",
    row: "h-[60px] text-text text-sm font-normal",
    cell: "px-2 py-2",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-text",
    footer: "mt-5",
    getRowStyle: (index) =>
      index % 2 === 0 ? "bg-paper" : "bg-paper bg-opacity-65",
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-2 py-2", headerClassName || ""),
    getCellStyle: (itemClassName) => twMerge("px-2 py-2", itemClassName || ""),
  },

  // the default variant for the Admin panel (= "default" with "admin_" color classes)
  admin: {
    container: "flex flex-col h-full",
    header: "flex flex-row justify-left items-center mb-2",
    title: "text-admin_text text-xl font-semibold",
    searchInput:
      "text-admin_text bg-transparent outline-none ml-10 flex-1 w-[200px] h-[30px] px-2 py-1 rounded",
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-auto flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper",
    table: "w-full table-fixed md:table-auto",
    thead: "sticky top-0 z-10 bg-admin_paper text-admin_text border-b border-admin_bordered",
    headerRow: "text-left text-admin_text font-normal",
    headerCell: "px-2 py-2",
    row: "h-[60px] text-admin_text text-sm font-normal",
    cell: "px-2 py-2",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-admin_text",
    footer: "mt-5",
    getRowStyle: (index) =>
      index % 2 === 0 ? "bg-admin_paper" : "bg-admin_canvas",
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-2 py-2", headerClassName || ""),
    getCellStyle: (itemClassName) => twMerge("px-2 py-2", itemClassName || ""),
  },

  browser: {
    container: "relative w-full flex flex-col h-[90vh] justify-start bg-paper rounded-3xl p-6 pb-4",
    header: "w-full flex justify-between items-end gap-2 my-5",
    title: "absolute top-4 text-text text-xl font-semibold",
    searchInput: "absolute top-2 right-4 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
    headerControls: "w-full flex justify-end",
    tableWrapper:
      "flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper",
    table: "w-full table-fixed md:table-auto",
    thead: "sticky top-0 z-10 bg-paper text-text border-b border-bordered",
    headerRow: "text-left text-text font-normal",
    headerCell: "px-2 py-2",
    row: "h-[60px] text-text text-sm font-normal",
    cell: "px-2 py-2",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-text",
    footer: "mt-auto",
    getRowStyle: (index) =>
      index % 2 === 0 ? "bg-paper" : "bg-canvas",
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-2 py-1", headerClassName || ""),
    getCellStyle: (itemClassName) => twMerge("px-2 py-2", itemClassName || ""),
  },

  // Used for CMS, Localization and CS browser screens
  admin_browser: {
    container: "relative w-full flex flex-col h-[82vh] justify-start bg-admin_paper rounded-3xl p-6 pb-4 overflow-hidden",
    header: "w-full flex justify-between items-end gap-2 my-4",
    title: "absolute top-4 text-admin_text text-xl font-semibold",
    searchInput: "absolute top-2 right-4 text-admin_text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
    headerControls: "w-full flex justify-end",
    tableWrapper:
      "flex flex-1 min-h-0 flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper",
    table: "w-full table-fixed md:table-auto",
    thead: "sticky top-0 z-10 bg-admin_paper text-admin_text border-b border-admin_bordered",
    headerRow: "text-left text-admin_text font-normal",
    headerCell: "px-2 py-2",
    row: "h-[60px] text-admin_text text-sm font-normal",
    cell: "px-2 py-2",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-admin_text",
    footer: "mt-auto",
    getRowStyle: (index) =>
      index % 2 === 0 ? "bg-admin_paper" : "bg-admin_canvas",
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-2 py-1", headerClassName || ""),
    getCellStyle: (itemClassName) => twMerge("px-2 py-2", itemClassName || ""),
  },

  // Compact variant - Smaller rows and tighter spacing
  compact: {
    container: "flex flex-col h-full",
    header: "flex flex-row justify-left items-center px-0.5 mt-1 mb-2",
    title: "text-text text-lg font-medium",
    searchInput: "ml-6 flex-1 w-[180px]", // Simplified
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-auto flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper",
    table: "w-full table-fixed md:table-auto",
    thead: "sticky top-0 z-10 bg-paper text-text",
    headerRow: "text-left bg-paper text-text font-normal text-sm",
    headerCell: "px-2 py-1",
    row: "h-[40px] text-text text-xs font-normal",
    cell: "px-2 py-1",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-text text-sm",
    footer: "mt-3",
    getRowStyle: (index) =>
      index % 2 === 0 ? "bg-paper" : "bg-paper bg-opacity-65",
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-2 py-1 text-sm", headerClassName || ""),
    getCellStyle: (itemClassName) => twMerge("px-2 py-1", itemClassName || ""),
  },

  admin_compact: {
    container: "flex flex-col h-full",
    header: "flex flex-row justify-left items-center px-0.5 mt-1 mb-2",
    title: "text-admin_text text-lg font-medium",
    searchInput: "ml-6 flex-1 w-[180px]", // Simplified
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-auto flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper",
    table: "w-full table-fixed md:table-auto",
    thead: "sticky top-0 z-10 bg-admin_paper text-admin_text",
    headerRow: "text-left bg-admin_paper text-admin_text font-normal text-sm",
    headerCell: "px-2 py-1",
    row: "h-[40px] text-admin_text text-xs font-normal",
    cell: "px-2 py-1",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-admin_text text-sm",
    footer: "mt-3",
    getRowStyle: (index) =>
      index % 2 === 0 ? "bg-admin_paper" : "bg-admin_paper bg-opacity-65",
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-2 py-1 text-sm", headerClassName || ""),
    getCellStyle: (itemClassName) => twMerge("px-2 py-1", itemClassName || ""),
  },

  // Cards variant - Card-like rows with shadows
  cards: {
    container: "flex flex-col h-full bg-paper rounded-3xl p-6",
    header: "flex flex-row justify-left items-center mb-4",
    title:
      "text-text text-[24px] md:text-[32px] lg:text-[36px] leading-tight font-bold tracking-custom_1 pl-4",
    searchInput: "ml-10 flex-1 w-[220px]", // Simplified
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-auto flex-col overflow-x-hidden overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper",
    table:
      "w-full table-fixed md:table-auto border-separate border-spacing-y-0",
    thead: "sticky top-0 z-10 text-text",
    headerRow:
      "text-left text-text text-sm md:text-base xl:text-[18px] font-semibold align-top",
    headerCell: "px-4 py-2",
    row: "h-[40px] text-text text-sm xl:text-base font-normal my-0 transition-all duration-200",
    cell: "px-4",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-text font-medium",
    footer: "mt-0",
    getRowStyle: () => "bg-transparent shadow-sm hover:shadow-md rounded-lg",
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-4 py-2", headerClassName || ""),
    getCellStyle: (itemClassName) => twMerge("px-4 py-0", itemClassName || ""),
  },

  admin_cards: {
    container: "flex flex-col h-full bg-admin_paper rounded-3xl p-6",
    header: "flex flex-row justify-left items-center mb-4",
    title:
      "text-admin_text text-2xl leading-tight font-bold tracking-custom_1 pl-4",
    searchInput: "ml-10 flex-1 w-[220px]", // Simplified
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-auto flex-col overflow-x-hidden overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper",
    table:
      "w-full table-fixed md:table-auto border-separate border-spacing-y-0",
    thead: "sticky top-0 z-10 text-admin_text",
    headerRow:
      "text-left text-admin_text text-sm md:text-base xl:text-[18px] font-semibold align-top",
    headerCell: "px-4 py-2",
    row: "h-[40px] text-admin_text text-sm xl:text-base font-normal my-0 transition-all duration-200",
    cell: "px-4",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-admin_text font-medium",
    footer: "mt-0",
    getRowStyle: () => "bg-transparent shadow-sm hover:shadow-md rounded-lg",
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-4 py-2", headerClassName || ""),
    getCellStyle: (itemClassName) => twMerge("px-4 py-0", itemClassName || ""),
  },

  // Dashboard variant - Optimized for dashboard displays
  dashboard: {
    container: "flex flex-col h-full",
    header: "flex flex-row justify-left items-center mb-3",
    title: "text-text text-lg font-semibold",
    searchInput:
      "text-text bg-canvas outline-none ml-8 flex-1 w-[200px] h-[30px] px-2 py-1 border border-bordered rounded",
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-[300px] flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper",
    table: "w-full table-fixed md:table-auto",
    thead: "sticky top-0 z-10 bg-paper text-text",
    headerRow: "text-left bg-neutral_1/50 text-text font-medium",
    headerCell: "px-3 py-2",
    row: "h-[55px] text-text text-sm font-normal transition-colors duration-200",
    cell: "px-3 py-2",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-text",
    footer: "mt-4",
    getRowStyle: (index) =>
      twMerge(
        index % 2 === 0 ? "bg-paper" : "bg-paper bg-opacity-65",
        "hover:bg-neutral_1"
      ),
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-3 py-2", headerClassName || ""),
    getCellStyle: (itemClassName) => twMerge("px-3 py-2", itemClassName || ""),
  },

  admin_dashboard: {
    container: "flex flex-col h-full",
    header: "flex flex-row justify-left items-center mb-3",
    title: "text-admin_text text-lg font-semibold",
    searchInput:
      "text-admin_text bg-admin_canvas outline-none ml-8 flex-1 w-[200px] h-[30px] px-2 py-1 border border-admin_bordered rounded",
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-[300px] flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper",
    table: "w-full table-fixed md:table-auto",
    thead: "sticky top-0 z-10 bg-admin_paper text-admin_text",
    headerRow: "text-left bg-admin_neutral_1/50 text-admin_text font-medium",
    headerCell: "px-3 py-2",
    row: "h-[55px] text-admin_text text-sm font-normal transition-colors duration-200",
    cell: "px-3 py-2",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-admin_text",
    footer: "mt-4",
    getRowStyle: (index) =>
      twMerge(
        index % 2 === 0 ? "bg-admin_paper" : "bg-admin_paper bg-opacity-65",
        "hover:bg-admin_neutral_1"
      ),
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-3 py-2", headerClassName || ""),
    getCellStyle: (itemClassName) => twMerge("px-3 py-2", itemClassName || ""),
  },

  // Report variant - Formal styling for reports
  report: {
    container: "flex flex-col h-full",
    header: "flex flex-row justify-left items-center mb-5",
    title: "text-text text-2xl font-bold",
    searchInput:
      "text-text bg-canvas outline-none ml-12 flex-1 w-[250px] h-[35px] px-3 py-2 border border-neutral_2 rounded",
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-auto flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper",
    table: "w-full table-fixed md:table-auto",
    thead: "sticky top-0 z-10 bg-paper text-text",
    headerRow: "text-left bg-neutral_1/50 text-text font-semibold",
    headerCell: "px-4 py-3 border-b-2 border-neutral_2",
    row: "h-[65px] text-text text-sm font-normal",
    cell: "px-4 py-3 border-b border-neutral_1",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-text font-medium",
    footer: "mt-6",
    getRowStyle: (index) =>
      twMerge("bg-paper", index % 2 === 0 ? "bg-opacity-100" : "bg-opacity-50"),
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-4 py-3 border-b-2 border-neutral_2", headerClassName || ""),
    getCellStyle: (itemClassName) =>
      twMerge("px-4 py-3 border-b border-neutral_1", itemClassName || ""),
  },

  admin_report: {
    container: "flex flex-col h-full",
    header: "flex flex-row justify-left items-center mb-5",
    title: "text-admin_text text-2xl font-bold",
    searchInput:
      "text-admin_text bg-admin_canvas outline-none ml-12 flex-1 w-[250px] h-[35px] px-3 py-2 border border-admin_bordered rounded",
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-auto flex-col overflow-y-auto scrollbar-thin scrollbar-admin_thumb-neutral_2 scrollbar-track-admin_paper",
    table: "w-full table-fixed md:table-auto",
    thead: "sticky top-0 z-10 bg-admin_paper text-admin_text",
    headerRow: "text-left bg-admin_neutral_1/50 text-admin_text font-semibold",
    headerCell: "px-4 py-3 border-b-2 border-admin_bordered",
    row: "h-[65px] text-admin_text text-sm font-normal",
    cell: "px-4 py-3 border-b border-admin_neutral_1",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-admin_text font-medium",
    footer: "mt-6",
    getRowStyle: (index) =>
      twMerge("bg-admin_paper", index % 2 === 0 ? "bg-opacity-100" : "bg-opacity-50"),
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-4 py-3 border-b-2 border-admin_bordered", headerClassName || ""),
    getCellStyle: (itemClassName) =>
      twMerge("px-4 py-3 border-b border-admin_neutral_1", itemClassName || ""),
  },

  // Simple variant - Clean and minimal styling
  simple: {
    container: "flex flex-col h-full",
    header: "flex flex-row justify-left items-center mb-2",
    title: "text-text text-lg font-medium",
    searchInput:
      "text-text bg-canvas outline-none ml-8 flex-1 w-[200px] h-[30px] px-2 py-1 border border-bordered rounded",
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-auto flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper",
    table: "w-full table-fixed md:table-auto",
    thead: "sticky top-0 z-10 bg-paper text-text",
    headerRow: "text-left text-text font-medium",
    headerCell: "px-3 py-2 border-b border-bordered",
    row: "h-[50px] text-text text-sm font-normal border-b border-neutral_1",
    cell: "px-3 py-2",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-text",
    footer: "mt-4",
    getRowStyle: () => "hover:bg-neutral_1 transition-colors duration-200",
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-3 py-2 border-b border-bordered", headerClassName || ""),
    getCellStyle: (itemClassName) => twMerge("px-3 py-2", itemClassName || ""),
  },

  admin_simple: {
    container: "flex flex-col h-full",
    header: "flex flex-row justify-left items-center mb-2",
    title: "text-admin_text text-lg font-medium",
    searchInput:
      "ml-8 flex-1 w-[200px]",
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-auto flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper",
    table: "w-full table-fixed md:table-auto",
    thead: "sticky top-0 z-10 bg-admin_paper text-admin_text",
    headerRow: "text-left text-admin_text font-medium",
    headerCell: "px-3 py-2 border-b border-admin_bordered",
    row: "h-[50px] text-admin_text text-sm font-normal",
    cell: "px-3 py-2",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-admin_text",
    footer: "mt-4",
    getRowStyle: () => "",
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-3 py-2 border-b border-admin_bordered", headerClassName || ""),
    getCellStyle: (itemClassName) => twMerge("px-3 py-2", itemClassName || ""),
  },

  // Minimal variant - Most basic styling without footer
  minimal: {
    container: "flex flex-col h-full",
    header: "flex flex-row justify-left items-center mb-2",
    title: "text-text text-base font-medium",
    searchInput:
      "text-text bg-canvas outline-none ml-6 flex-1 w-[180px] h-[28px] px-2 py-1 border border-bordered rounded",
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-auto flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper",
    table: "w-full table-fixed md:table-auto",
    thead: "sticky top-0 z-10 bg-paper text-text",
    headerRow: "text-left text-text font-normal",
    headerCell: "px-2 py-2",
    row: "h-[45px] text-text text-sm font-normal",
    cell: "px-2 py-2",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-text text-sm",
    footer: "mt-3",
    getRowStyle: () => "border-b border-neutral_1",
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-2 py-2", headerClassName || ""),
    getCellStyle: (itemClassName) => twMerge("px-2 py-2", itemClassName || ""),
  },

  admin_minimal: {
    container: "flex flex-col h-full",
    header: "flex flex-row justify-left items-center mb-2",
    title: "text-admin_text text-base font-medium",
    searchInput:
      "text-admin_text bg-admin_canvas outline-none ml-6 flex-1 w-[180px] h-[28px] px-2 py-1 border border-admin_bordered rounded",
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-auto flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper",
    table: "w-full table-fixed md:table-auto",
    thead: "sticky top-0 z-10 bg-admin_paper text-admin_text",
    headerRow: "text-left text-admin_text font-normal",
    headerCell: "px-2 py-2",
    row: "h-[45px] text-admin_text text-sm font-normal",
    cell: "px-2 py-2",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-admin_text text-sm",
    footer: "mt-3",
    getRowStyle: () => "border-b border-admin_neutral_1",
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-2 py-2", headerClassName || ""),
    getCellStyle: (itemClassName) => twMerge("px-2 py-2", itemClassName || ""),
  },

  // Dense variant - Compact layout with borders
  dense: {
    container: "flex flex-col h-full",
    header: "flex flex-row justify-left items-center mb-1",
    title: "text-text text-base font-medium",
    searchInput:
      "text-text bg-canvas outline-none ml-4 flex-1 w-[160px] h-[25px] px-2 py-1 border border-bordered rounded text-sm",
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-auto flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper",
    table: "w-full table-fixed md:table-auto border-collapse",
    thead: "sticky top-0 z-10 bg-paper text-text",
    headerRow: "text-left bg-neutral_1/50 text-text font-medium text-xs",
    headerCell: "px-2 py-1 border border-neutral_2",
    row: "h-[35px] text-text text-xs font-normal",
    cell: "px-2 py-1 border border-neutral_1",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-text text-xs",
    footer: "mt-2",
    getRowStyle: (index) =>
      index % 2 === 0 ? "bg-paper" : "bg-paper bg-opacity-65",
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-2 py-1 border border-neutral_2", headerClassName || ""),
    getCellStyle: (itemClassName) =>
      twMerge("px-2 py-1 border border-neutral_1", itemClassName || ""),
  },

  admin_dense: {
    container: "flex flex-col h-full",
    header: "flex flex-row justify-left items-center mb-1",
    title: "text-admin_text text-base font-medium",
    searchInput:
      "text-admin_text bg-admin_canvas outline-none ml-4 flex-1 w-[160px] h-[25px] px-2 py-1 border border-admin_bordered rounded text-sm",
    headerControls: "flex-grow flex justify-end",
    tableWrapper:
      "h-auto flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper",
    table: "w-full table-fixed md:table-auto border-collapse",
    thead: "sticky top-0 z-10 bg-admin_paper text-admin_text",
    headerRow: "text-left bg-admin_neutral_1/50 text-admin_text font-medium text-xs",
    headerCell: "px-2 py-1 border border-admin_neutral_2",
    row: "h-[35px] text-admin_text text-xs font-normal",
    cell: "px-2 py-1 border border-admin_neutral_1",
    noDataContainer: "flex items-center justify-center w-full h-full",
    noDataText: "text-admin_text text-xs",
    footer: "mt-2",
    getRowStyle: (index) =>
      index % 2 === 0 ? "bg-admin_paper" : "bg-admin_paper bg-opacity-65",
    getHeaderCellStyle: (headerClassName) =>
      twMerge("px-2 py-1 border border-admin_neutral_2", headerClassName || ""),
    getCellStyle: (itemClassName) =>
      twMerge("px-2 py-1 border border-admin_neutral_1", itemClassName || ""),
  },
}
